import { defaultLocaleForPrefix } from "~/utils/marketSegments";

export type Locale = "vi" | "en" | "th";

export function localePath(path: string, locale: Locale, marketPrefix: string): string {
  // Locale never prefixes (vi/en are cookie-based, no /en/ route variants).
  // MARKET can: path-prefixed markets (/th) must carry their prefix on every
  // internal href or navigation silently drops back to the root market
  // (Thailand v7.1 Phase 1). marketPrefix is REQUIRED so the type-checker
  // enumerates every call site; components source it from useMarketPrefix(),
  // server code from getForcedMarketPath(request).
  if (!marketPrefix) return path;
  if (path === marketPrefix || path.startsWith(marketPrefix + "/")) return path;
  return marketPrefix + path;
}

/**
 * T3.9: market vs locale precedence rules.
 *
 * Two independent cookies govern the storefront experience:
 *   - `eql_market`: determines pricing + shipping zones (VN vs intl).
 *   - `eql_locale`: determines displayed language (vi vs en).
 *
 * These are DELIBERATELY independent. A Vietnamese customer visiting from
 * abroad may want vi labels with USD pricing; a foreign expat may want en
 * labels with VND pricing. We respect whatever combination the user has
 * explicitly set, and only fall back to defaults when a cookie is missing.
 *
 * The defaults (applied on first visit or when a cookie is missing):
 *   1. `eql_market`: determined by Cloudflare's CF-IPCountry header in
 *      `app/services/markets/marketDetection.server.ts`. VN → market_vn,
 *      everything else → market_intl.
 *   2. `eql_locale`: starts at "vi" for the VN market default; if the user
 *      lands on market_intl AND their Accept-Language header prefers en,
 *      the locale switch banner offers them English.
 *
 * `detectLocale` below ONLY reads the locale cookie (and the legacy /en/
 * URL prefix). It intentionally does NOT consult the market cookie — market
 * and locale are orthogonal and one should never silently override the other.
 */
export function detectLocale(url: string, cookieHeader?: string): Locale {
  const pathname = new URL(url).pathname;
  // Check URL path first (for backward compatibility with /en/ links)
  if (pathname.startsWith("/en")) return "en";
  // Then check cookie
  if (cookieHeader) {
    const match = cookieHeader.match(/eql_locale=(\w+)/);
    if (match && (match[1] === "en" || match[1] === "th")) return match[1] as Locale;
  }
  // No explicit choice: a path-prefixed market surface defaults to the
  // market's chrome language, not vi. Child-route loaders call this
  // directly (meta titles/descriptions), and the vi fallback was leaking
  // Vietnamese <title>s onto every /th page (2026-07-03). server.mjs
  // never rewrites the URL, so the prefix is visible here.
  const seg = pathname.split("/")[1];
  const marketDefault = defaultLocaleForPrefix(seg || null);
  if (marketDefault) return marketDefault;
  return "vi";
}

/**
 * T3.8: parse an Accept-Language header with quality weights and return
 * the preferred locale ("vi" or "en"), or null if the header doesn't
 * express a preference we should act on.
 *
 * Examples:
 *   "vi-VN,en;q=0.9"          → "vi"   (vi has implicit q=1.0)
 *   "en-US,en;q=0.9,vi;q=0.5" → "en"
 *   "fr-FR,de;q=0.8"          → null   (neither supported)
 *   ""                        → null   (no header; do not override market default)
 *   "vi"                      → "vi"
 *
 * IMPORTANT: returns `null` (not a default) when the caller has no signal.
 * The T3.8 fix above (in _storefront.tsx) uses this to ONLY redirect when
 * the browser explicitly prefers English. Empty headers (e.g. curl, bots,
 * some iOS WebViews) must NOT trigger a locale override — otherwise every
 * first-visit request enters a redirect loop.
 */
export function parseAcceptLanguage(header: string): Locale | null {
  if (!header) return null;

  type Entry = { lang: string; q: number };
  const entries: Entry[] = header
    .split(",")
    .map((raw) => {
      const [tag, ...params] = raw.trim().split(";");
      const lang = (tag || "").toLowerCase().split("-")[0];
      let q = 1;
      for (const p of params) {
        const [k, v] = p.trim().split("=");
        if (k === "q" && v) {
          const parsed = parseFloat(v);
          if (!Number.isNaN(parsed)) q = parsed;
        }
      }
      return { lang, q };
    })
    .filter((e) => e.lang);

  // Pick the highest-weighted supported language. Stable sort by q desc.
  entries.sort((a, b) => b.q - a.q);
  for (const e of entries) {
    if (e.lang === "vi") return "vi";
    if (e.lang === "en") return "en";
  }
  return null;
}
